import React, { useEffect, useState, useRef } from 'react';
import { PlusIcon, SearchIcon, CalendarIcon, ListIcon, ChevronLeftIcon, ChevronRightIcon, XIcon, RefreshCwIcon, CheckIcon, DownloadIcon, ClockIcon, LockIcon, UserIcon, EditIcon } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import { Modal } from '../ui/Modal';
import { Badge, getStatusBadgeVariant } from '../ui/Badge';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { Appointment } from '../../types';
import { formatTime, getToday, getTomorrow, getDaysInMonth, getFirstDayOfMonth, getMonthName, getStatusName, getDayOfWeekKey, getDayOfWeekName, getDoctorPrefix, formatCurrency } from '../../utils/helpers';
import { validateAppointmentForm } from '../../utils/validation';
import { exportAppointmentsToExcel } from '../../utils/excelExport';
import { exportAppointmentsToPdf } from '../../utils/pdfExport';
import { usePagination } from '../../hooks/usePagination';
import { useSortableData } from '../../hooks/useSortableData';
import { Pagination } from '../ui/Pagination';
import { SortableHeader } from '../ui/SortableHeader';
import { emailNotificationService } from '../../utils/emailNotifications';
import { sendDesktopNotification } from '../../utils/desktopNotifications';
import { useRolePermissions } from '../../hooks/useRolePermissions';
import { FormSection } from '../ui/FormSection';
import { ExcelExportModal } from '../ui/ExcelExportModal';
import { PdfExportModal } from '../ui/PdfExportModal';
interface AppointmentsProps {
  initialDateFilter?: string;
}
export function Appointments({
  initialDateFilter
}: AppointmentsProps) {
  const {
    appointments,
    addAppointment,
    updateAppointment,
    cancelAppointment,
    rescheduleAppointment,
    completeAppointment,
    patients,
    doctors,
    services,
    specialties,
    getPatient,
    getDoctor,
    getService,
    getSpecialty,
    getDoctorsBySpecialty,
    checkDoctorAvailability,
    getAppointmentsByDate
  } = useData();
  const {
    user
  } = useAuth();
  const {
    addNotification
  } = useNotifications();
  // ✅ Usar hook centralizado de permisos
  const {
    isAdmin,
    canExport,
    getPermissionLevel
  } = useRolePermissions();
  const canExportExcel = canExport('appointments');
  const canExportPdf = canExport('appointments');
  const appointmentPermissionLevel = getPermissionLevel('appointments');
  const isOwnOnly = appointmentPermissionLevel === 'own';
  const isReadOnlyAppointments = appointmentPermissionLevel === 'read';
  const canCreateAppointments = appointmentPermissionLevel === 'full';
  const linkedDoctor = isOwnOnly && user ? doctors.find(d => d.email.toLowerCase().trim() === user.email.toLowerCase().trim()) : null;
  const hasOwnButNoDoctor = isOwnOnly && !linkedDoctor;
  const [activeView, setActiveView] = useState<'list' | 'calendar'>('list');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDate, setFilterDate] = useState(initialDateFilter || '');
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  const [pickerNavDate, setPickerNavDate] = useState(new Date());
  useEffect(() => {
    if (!isDatePickerOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setIsDatePickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDatePickerOpen]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const [isRescheduleOpen, setIsRescheduleOpen] = useState(false);
  const [isCompleteOpen, setIsCompleteOpen] = useState(false);
  // ─── NEW: action-select modal state ───────────────────────────────────────
  const [isActionSelectOpen, setIsActionSelectOpen] = useState(false);
  // ──────────────────────────────────────────────────────────────────────────
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [editingAppointment, setEditingAppointment] = useState<Appointment | null>(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(getToday());
  const [formData, setFormData] = useState({
    patientId: '',
    doctorId: '',
    serviceIds: [] as string[],
    date: getToday(),
    time: '09:00',
    notes: ''
  });
  const [selectedSpecialty, setSelectedSpecialty] = useState('');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [cancelReason, setCancelReason] = useState('');
  const [rescheduleData, setRescheduleData] = useState({
    date: '',
    time: ''
  });
  const [completeData, setCompleteData] = useState({
    nextAppointmentDate: '',
    nextAppointmentTime: '',
    nextAppointmentNotes: '',
    nextServiceIds: [] as string[]
  });
  const [isExporting, setIsExporting] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const [isPdfExportModalOpen, setIsPdfExportModalOpen] = useState(false);
  const [serviceSearchTerm, setServiceSearchTerm] = useState('');
  const [doctorStatuses, setDoctorStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  const [specialtyStatuses, setSpecialtyStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  const [serviceStatuses, setServiceStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  const [patientStatuses, setPatientStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  useEffect(() => {
    const loadStatuses = () => {
      try {
        const ds = localStorage.getItem('savidna-doctors-status');
        if (ds) setDoctorStatuses(JSON.parse(ds));
        const ss = localStorage.getItem('savidna-specialties-status');
        if (ss) setSpecialtyStatuses(JSON.parse(ss));
        const svs = localStorage.getItem('savidna-services-status');
        if (svs) setServiceStatuses(JSON.parse(svs));
        const ps = localStorage.getItem('savidna-patients-status');
        if (ps) setPatientStatuses(JSON.parse(ps));
      } catch {}
    };
    loadStatuses();
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'savidna-doctors-status' && e.newValue) {
        try {
          setDoctorStatuses(JSON.parse(e.newValue));
        } catch {}
      }
      if (e.key === 'savidna-specialties-status' && e.newValue) {
        try {
          setSpecialtyStatuses(JSON.parse(e.newValue));
        } catch {}
      }
      if (e.key === 'savidna-services-status' && e.newValue) {
        try {
          setServiceStatuses(JSON.parse(e.newValue));
        } catch {}
      }
      if (e.key === 'savidna-patients-status' && e.newValue) {
        try {
          setPatientStatuses(JSON.parse(e.newValue));
        } catch {}
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);
  const isDoctorActive = (id: string) => !doctorStatuses[id];
  const isSpecialtyActive = (id: string) => !specialtyStatuses[id];
  const isServiceActive = (id: string) => !serviceStatuses[id];
  const isPatientActive = (id: string) => !patientStatuses[id];
  const today = getToday();
  const filteredAppointments = appointments.filter(apt => {
    const patient = getPatient(apt.patientId);
    const doctor = getDoctor(apt.doctorId);
    const matchesSearch = !searchTerm || patient?.name.toLowerCase().includes(searchTerm.toLowerCase()) || doctor?.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !filterStatus || apt.status === filterStatus;
    const matchesDate = !filterDate || apt.date === filterDate;
    const matchesOwnership = isOwnOnly && linkedDoctor ? apt.doctorId === linkedDoctor.id : true;
    return matchesSearch && matchesStatus && matchesDate && matchesOwnership;
  });
  const {
    sortedItems: sortedAppointments,
    requestSort: aptRequestSort,
    isSorted: aptIsSorted
  } = useSortableData(filteredAppointments);
  const {
    paginatedItems: paginatedAppointments,
    currentPage: aptPage,
    totalPages: aptTotalPages,
    totalItems: aptTotalItems,
    startIndex: aptStartIndex,
    endIndex: aptEndIndex,
    goToPage: aptGoToPage,
    nextPage: aptNextPage,
    prevPage: aptPrevPage
  } = usePagination(sortedAppointments);
  const statusOptions = [{
    value: '',
    label: 'Todos los estados'
  }, {
    value: 'programada',
    label: 'Programada'
  }, {
    value: 'completada',
    label: 'Completada'
  }, {
    value: 'cancelada',
    label: 'Cancelada'
  }, {
    value: 'reprogramada',
    label: 'Reprogramada'
  }];
  const patientOptions = patients.filter(p => isPatientActive(p.id)).map(p => ({
    value: p.id,
    label: p.name
  }));
  const specialtyOptions = [{
    value: '',
    label: 'Todas las especialidades'
  }, ...specialties.filter(s => {
    if (!isSpecialtyActive(s.id)) return false;
    if (formData.doctorId) {
      const selectedDoctor = doctors.find(d => d.id === formData.doctorId);
      return selectedDoctor ? s.id === selectedDoctor.specialtyId : true;
    }
    return true;
  }).map(s => ({
    value: s.id,
    label: s.name
  }))];
  const doctorOptions = selectedSpecialty ? getDoctorsBySpecialty(selectedSpecialty).filter(d => isDoctorActive(d.id)).map(d => ({
    value: d.id,
    label: `${getDoctorPrefix(d.gender, getSpecialty(d.specialtyId)?.name)} ${d.name}`
  })) : doctors.filter(d => isDoctorActive(d.id)).map(d => ({
    value: d.id,
    label: `${getDoctorPrefix(d.gender, getSpecialty(d.specialtyId)?.name)} ${d.name}`
  }));
  const availableServices = services.filter(s => isServiceActive(s.id));
  const filteredFormServices = availableServices.filter(s => serviceSearchTerm ? s.name.toLowerCase().includes(serviceSearchTerm.toLowerCase()) : true);
  const toggleService = (serviceId: string) => {
    setFormData(prev => {
      const currentIds = prev.serviceIds || [];
      const newIds = currentIds.includes(serviceId) ? currentIds.filter(id => id !== serviceId) : [...currentIds, serviceId];
      return {
        ...prev,
        serviceIds: newIds
      };
    });
  };
  const selectedServicesList = availableServices.filter(s => formData.serviceIds.includes(s.id));
  const totalDuration = selectedServicesList.reduce((sum, s) => sum + s.duration, 0);
  const totalPrice = selectedServicesList.reduce((sum, s) => sum + s.price, 0);
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const calendarDays: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) calendarDays.push(null);
  for (let i = 1; i <= daysInMonth; i++) calendarDays.push(i);
  const getDateString = (day: number) => `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  const getAppointmentCountForDay = (day: number) => {
    const dateStr = getDateString(day);
    return appointments.filter(a => {
      const matchesDate = a.date === dateStr && a.status !== 'cancelada';
      const matchesOwnership = isOwnOnly && linkedDoctor ? a.doctorId === linkedDoctor.id : true;
      return matchesDate && matchesOwnership;
    }).length;
  };
  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const selectedDateAppointments = getAppointmentsByDate(selectedDate);
  const openNewForm = () => {
    setSelectedAppointment(null);
    setFormData({
      patientId: '',
      doctorId: isOwnOnly && linkedDoctor ? linkedDoctor.id : '',
      serviceIds: [],
      date: getToday(),
      time: '09:00',
      notes: ''
    });
    setSelectedSpecialty('');
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openEditModal = (appointment: Appointment) => {
    setEditingAppointment(appointment);
    const serviceIds = appointment.serviceIds && appointment.serviceIds.length > 0 ? appointment.serviceIds : [appointment.serviceId];
    setFormData({
      patientId: appointment.patientId,
      doctorId: appointment.doctorId,
      serviceIds: serviceIds,
      date: appointment.date,
      time: appointment.time,
      notes: appointment.notes || ''
    });
    const doctor = getDoctor(appointment.doctorId);
    if (doctor) setSelectedSpecialty(doctor.specialtyId);
    setFormErrors({});
    setIsEditOpen(true);
  };
  const openCancelModal = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setCancelReason('');
    setIsCancelOpen(true);
  };
  const openRescheduleModal = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setRescheduleData({
      date: appointment.date,
      time: appointment.time
    });
    setIsRescheduleOpen(true);
  };
  const openCompleteModal = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    const currentServiceIds = appointment.serviceIds && appointment.serviceIds.length > 0 ? appointment.serviceIds : [appointment.serviceId].filter(Boolean);
    setCompleteData({
      nextAppointmentDate: '',
      nextAppointmentTime: appointment.time || '09:00',
      nextAppointmentNotes: '',
      nextServiceIds: [...currentServiceIds]
    });
    setServiceSearchTerm('');
    setIsCompleteOpen(true);
  };
  // ─── NEW: opens the action-select modal (entry point for the ✓ button) ────
  const openActionSelectModal = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setIsActionSelectOpen(true);
  };
  // ──────────────────────────────────────────────────────────────────────────
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validation = validateAppointmentForm(formData);
    if (!validation.isValid) {
      setFormErrors(validation.errors);
      return;
    }
    const selectedDoctor = getDoctor(formData.doctorId);
    if (selectedDoctor && selectedDoctor.schedule) {
      const dayKey = getDayOfWeekKey(formData.date) as keyof typeof selectedDoctor.schedule;
      const daySchedule = selectedDoctor.schedule[dayKey];
      const dayName = getDayOfWeekName(formData.date);
      const prefix = getDoctorPrefix(selectedDoctor.gender, getSpecialty(selectedDoctor.specialtyId)?.name);
      if (!daySchedule) {
        setFormErrors({
          date: `${prefix} ${selectedDoctor.name} no trabaja los ${dayName}`
        });
        return;
      }
      const requestedMinutes = parseInt(formData.time.split(':')[0]) * 60 + parseInt(formData.time.split(':')[1]);
      const startMinutes = parseInt(daySchedule.start.split(':')[0]) * 60 + parseInt(daySchedule.start.split(':')[1]);
      const endMinutes = parseInt(daySchedule.end.split(':')[0]) * 60 + parseInt(daySchedule.end.split(':')[1]);
      if (requestedMinutes < startMinutes || requestedMinutes >= endMinutes) {
        setFormErrors({
          time: `Fuera del horario. ${dayName}: ${formatTime(daySchedule.start)} - ${formatTime(daySchedule.end)}`
        });
        return;
      }
    }
    if (!checkDoctorAvailability(formData.doctorId, formData.date, formData.time)) {
      setFormErrors({
        time: 'El doctor ya tiene una cita en este horario o dentro del margen de 30 minutos entre citas'
      });
      return;
    }
    const patient = getPatient(formData.patientId);
    const doctor = getDoctor(formData.doctorId);
    const selectedServices = services.filter(s => formData.serviceIds.includes(s.id));
    const serviceNames = selectedServices.map(s => s.name).join(', ');
    const duration = selectedServices.reduce((sum, s) => sum + s.duration, 0) || 30;
    addAppointment({
      ...formData,
      serviceId: formData.serviceIds[0],
      duration,
      status: 'programada'
    });
    addNotification({
      type: 'appointment_created',
      title: 'Nueva Cita Creada',
      message: `Cita para ${patient?.name || 'Paciente'} con Dr. ${doctor?.name || 'Doctor'} el ${formData.date} a las ${formatTime(formData.time)} — ${serviceNames}`,
      relatedId: formData.patientId
    });
    // Desktop notification
    sendDesktopNotification({
      title: '🗓️ Nueva Cita Creada',
      body: `${patient?.name || 'Paciente'} con Dr. ${doctor?.name || 'Doctor'}\n${formData.date} a las ${formatTime(formData.time)}\n${serviceNames}`,
      tag: `appointment-created-${formData.patientId}-${formData.date}-${formData.time}`
    });
    if (patient && doctor && selectedServices.length > 0) {
      emailNotificationService.sendAppointmentCreated({
        patientName: patient.name,
        patientEmail: patient.email,
        doctorName: doctor.name,
        serviceName: serviceNames,
        date: formData.date,
        time: formData.time,
        duration,
        notes: formData.notes
      }).then(result => {
        if (result.success) {
          addNotification({
            type: 'appointment_created',
            title: '📧 Correo Enviado',
            message: `Notificación enviada a ${patient.email}`,
            relatedId: formData.patientId
          });
        } else if (emailNotificationService.isEnabled()) {
          addNotification({
            type: 'appointment_cancelled',
            title: '⚠️ Error de Correo',
            message: `No se pudo enviar correo a ${patient.email}: ${result.error || 'Error desconocido'}`,
            relatedId: formData.patientId
          });
        }
      }).catch(err => {
        console.error('Error enviando email de cita creada:', err);
        if (emailNotificationService.isEnabled()) {
          addNotification({
            type: 'appointment_cancelled',
            title: '⚠️ Error de Correo',
            message: `No se pudo enviar correo a ${patient.email}`,
            relatedId: formData.patientId
          });
        }
      });
    }
    setIsFormOpen(false);
  };
  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingAppointment) return;
    const validation = validateAppointmentForm(formData);
    if (!validation.isValid) {
      setFormErrors(validation.errors);
      return;
    }
    const selectedDoctor = getDoctor(formData.doctorId);
    if (selectedDoctor && selectedDoctor.schedule) {
      const dayKey = getDayOfWeekKey(formData.date) as keyof typeof selectedDoctor.schedule;
      const daySchedule = selectedDoctor.schedule[dayKey];
      const dayName = getDayOfWeekName(formData.date);
      const prefix = getDoctorPrefix(selectedDoctor.gender, getSpecialty(selectedDoctor.specialtyId)?.name);
      if (!daySchedule) {
        setFormErrors({
          date: `${prefix} ${selectedDoctor.name} no trabaja los ${dayName}`
        });
        return;
      }
      const requestedMinutes = parseInt(formData.time.split(':')[0]) * 60 + parseInt(formData.time.split(':')[1]);
      const startMinutes = parseInt(daySchedule.start.split(':')[0]) * 60 + parseInt(daySchedule.start.split(':')[1]);
      const endMinutes = parseInt(daySchedule.end.split(':')[0]) * 60 + parseInt(daySchedule.end.split(':')[1]);
      if (requestedMinutes < startMinutes || requestedMinutes >= endMinutes) {
        setFormErrors({
          time: `Fuera del horario. ${dayName}: ${formatTime(daySchedule.start)} - ${formatTime(daySchedule.end)}`
        });
        return;
      }
    }
    if (!checkDoctorAvailability(formData.doctorId, formData.date, formData.time, editingAppointment.id)) {
      setFormErrors({
        time: 'El doctor ya tiene una cita en este horario o dentro del margen de 30 minutos entre citas'
      });
      return;
    }
    const patient = getPatient(formData.patientId);
    const doctor = getDoctor(formData.doctorId);
    const selectedServices = services.filter(s => formData.serviceIds.includes(s.id));
    const serviceNames = selectedServices.map(s => s.name).join(', ');
    const duration = selectedServices.reduce((sum, s) => sum + s.duration, 0) || 30;
    await updateAppointment(editingAppointment.id, {
      patientId: formData.patientId,
      doctorId: formData.doctorId,
      serviceId: formData.serviceIds[0],
      serviceIds: formData.serviceIds,
      date: formData.date,
      time: formData.time,
      duration,
      notes: formData.notes
    });
    addNotification({
      type: 'appointment_rescheduled',
      title: 'Cita Actualizada',
      message: `La cita de ${patient?.name || 'Paciente'} ha sido actualizada.`,
      relatedId: editingAppointment.id
    });
    if (patient && doctor && selectedServices.length > 0) {
      emailNotificationService.sendAppointmentRescheduled({
        patientName: patient.name,
        patientEmail: patient.email,
        doctorName: doctor.name,
        serviceName: serviceNames,
        date: formData.date,
        time: formData.time,
        duration,
        oldDate: editingAppointment.date,
        oldTime: editingAppointment.time
      }).catch(err => console.error('Error enviando email de cita actualizada:', err));
    }
    setIsEditOpen(false);
    setEditingAppointment(null);
  };
  const handleCancel = () => {
    if (selectedAppointment) {
      const patient = getPatient(selectedAppointment.patientId);
      const doctor = getDoctor(selectedAppointment.doctorId);
      const service = getService(selectedAppointment.serviceId);
      cancelAppointment(selectedAppointment.id, cancelReason);
      addNotification({
        type: 'appointment_cancelled',
        title: 'Cita Cancelada',
        message: `La cita de ${patient?.name || 'Paciente'} con Dr. ${doctor?.name || 'Doctor'} del ${selectedAppointment.date} fue cancelada. Motivo: ${cancelReason || 'No especificado'}`,
        relatedId: selectedAppointment.id
      });
      if (patient && doctor && service) {
        emailNotificationService.sendAppointmentCancelled({
          patientName: patient.name,
          patientEmail: patient.email,
          doctorName: doctor.name,
          serviceName: service.name,
          date: selectedAppointment.date,
          time: selectedAppointment.time,
          duration: selectedAppointment.duration,
          cancelReason
        }).then(result => {
          if (result.success) {
            addNotification({
              type: 'appointment_created',
              title: '📧 Correo Enviado',
              message: `Notificación de cancelación enviada a ${patient.email}`,
              relatedId: selectedAppointment.id
            });
          } else if (emailNotificationService.isEnabled()) {
            addNotification({
              type: 'appointment_cancelled',
              title: '⚠️ Error de Correo',
              message: `No se pudo enviar correo de cancelación a ${patient.email}: ${result.error || 'Error desconocido'}`,
              relatedId: selectedAppointment.id
            });
          }
        }).catch(err => {
          console.error('Error enviando email de cita cancelada:', err);
          if (emailNotificationService.isEnabled()) {
            addNotification({
              type: 'appointment_cancelled',
              title: '⚠️ Error de Correo',
              message: `No se pudo enviar correo de cancelación a ${patient.email}`,
              relatedId: selectedAppointment.id
            });
          }
        });
      }
      setIsCancelOpen(false);
    }
  };
  const handleReschedule = () => {
    if (selectedAppointment) {
      if (!checkDoctorAvailability(selectedAppointment.doctorId, rescheduleData.date, rescheduleData.time, selectedAppointment.id)) {
        alert('El doctor no está disponible en este horario. Recuerda que hay un margen de 30 minutos entre citas.');
        return;
      }
      const patient = getPatient(selectedAppointment.patientId);
      const doctor = getDoctor(selectedAppointment.doctorId);
      const service = getService(selectedAppointment.serviceId);
      rescheduleAppointment(selectedAppointment.id, rescheduleData.date, rescheduleData.time);
      addNotification({
        type: 'appointment_rescheduled',
        title: 'Cita Reprogramada',
        message: `La cita de ${patient?.name || 'Paciente'} con Dr. ${doctor?.name || 'Doctor'} fue reprogramada de ${selectedAppointment.date} ${formatTime(selectedAppointment.time)} a ${rescheduleData.date} ${formatTime(rescheduleData.time)}`,
        relatedId: selectedAppointment.id
      });
      if (patient && doctor && service) {
        emailNotificationService.sendAppointmentRescheduled({
          patientName: patient.name,
          patientEmail: patient.email,
          doctorName: doctor.name,
          serviceName: service.name,
          date: rescheduleData.date,
          time: rescheduleData.time,
          duration: selectedAppointment.duration,
          oldDate: selectedAppointment.date,
          oldTime: selectedAppointment.time
        }).then(result => {
          if (result.success) {
            addNotification({
              type: 'appointment_created',
              title: '📧 Correo Enviado',
              message: `Notificación de reprogramación enviada a ${patient.email}`,
              relatedId: selectedAppointment.id
            });
          } else if (emailNotificationService.isEnabled()) {
            addNotification({
              type: 'appointment_cancelled',
              title: '⚠️ Error de Correo',
              message: `No se pudo enviar correo de reprogramación a ${patient.email}: ${result.error || 'Error desconocido'}`,
              relatedId: selectedAppointment.id
            });
          }
        }).catch(err => {
          console.error('Error enviando email de cita reprogramada:', err);
          if (emailNotificationService.isEnabled()) {
            addNotification({
              type: 'appointment_cancelled',
              title: '⚠️ Error de Correo',
              message: `No se pudo enviar correo de reprogramación a ${patient.email}`,
              relatedId: selectedAppointment.id
            });
          }
        });
      }
      setIsRescheduleOpen(false);
    }
  };
  const handleComplete = async () => {
    if (selectedAppointment) {
      try {
        const patient = getPatient(selectedAppointment.patientId);
        const doctor = getDoctor(selectedAppointment.doctorId);
        const serviceIds = selectedAppointment.serviceIds && selectedAppointment.serviceIds.length > 0 ? selectedAppointment.serviceIds : [selectedAppointment.serviceId];
        const selectedServices = services.filter(s => serviceIds.includes(s.id));
        const serviceNames = selectedServices.map(s => s.name).join(', ');
        await completeAppointment(selectedAppointment.id, completeData.nextAppointmentDate, completeData.nextAppointmentNotes);
        if (completeData.nextAppointmentDate && completeData.nextServiceIds.length > 0) {
          setTimeout(() => {
            const followUpApts = appointments.filter(a => a.patientId === selectedAppointment.patientId && a.date === completeData.nextAppointmentDate && a.status === 'programada');
            const followUp = followUpApts[followUpApts.length - 1];
            if (followUp) {
              const nextServices = services.filter(s => completeData.nextServiceIds.includes(s.id));
              const nextDuration = nextServices.reduce((sum, s) => sum + s.duration, 0) || 30;
              updateAppointment(followUp.id, {
                serviceId: completeData.nextServiceIds[0],
                serviceIds: completeData.nextServiceIds,
                time: completeData.nextAppointmentTime || followUp.time,
                duration: nextDuration
              });
            }
          }, 500);
        }
        const nextInfo = completeData.nextAppointmentDate ? ` — Próxima cita: ${completeData.nextAppointmentDate}` : '';
        addNotification({
          type: 'appointment_created',
          title: 'Cita Completada',
          message: `La cita de ${patient?.name || 'Paciente'} con Dr. ${doctor?.name || 'Doctor'} (${serviceNames}) fue completada exitosamente.${nextInfo}`,
          relatedId: selectedAppointment.id
        });
        // Desktop notification
        sendDesktopNotification({
          title: '✅ Cita Completada',
          body: `${patient?.name || 'Paciente'} con Dr. ${doctor?.name || 'Doctor'}\n${serviceNames}${nextInfo}`,
          tag: `appointment-completed-${selectedAppointment.id}`
        });
        setIsCompleteOpen(false);
      } catch (error) {
        console.error('Error completando cita:', error);
        alert('Error al completar la cita. Intente nuevamente.');
      }
    }
  };
  const handleExportToExcel = async (dateFrom: string | null, dateTo: string | null) => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    let dataToExport = appointments;
    if (dateFrom && dateTo) {
      dataToExport = appointments.filter(a => a.date >= dateFrom && a.date <= dateTo);
    }
    exportAppointmentsToExcel(dataToExport, (patientId: string) => getPatient(patientId)?.name || '-', (doctorId: string) => getDoctor(doctorId)?.name || '-', (serviceId: string) => getService(serviceId)?.name || '-');
    setIsExporting(false);
    setIsExportModalOpen(false);
  };
  const handleExportToPdf = async (dateFrom: string | null, dateTo: string | null) => {
    setIsPdfExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    let dataToExport = appointments;
    if (dateFrom && dateTo) {
      dataToExport = appointments.filter(a => a.date >= dateFrom && a.date <= dateTo);
    }
    exportAppointmentsToPdf(dataToExport, (patientId: string) => getPatient(patientId)?.name || '-', (doctorId: string) => getDoctor(doctorId)?.name || '-', (serviceId: string) => getService(serviceId)?.name || '-');
    setIsPdfExporting(false);
    setIsPdfExportModalOpen(false);
  };
  const viewTabs = [{
    id: 'list',
    label: 'Lista',
    icon: <ListIcon className="w-4 h-4" data-id="element-189" />
  }, {
    id: 'calendar',
    label: 'Calendario',
    icon: <CalendarIcon className="w-4 h-4" data-id="element-190" />
  }];
  const renderAppointmentForm = (isEdit = false) => <div className="space-y-6" data-id="element-191">
      <FormSection title="Paciente" data-id="element-192">
        <Select label="Seleccionar Paciente" options={patientOptions} value={formData.patientId} onChange={value => setFormData({
        ...formData,
        patientId: value
      })} error={formErrors.patientId} searchable placeholder="Buscar paciente..." data-id="element-193" />
      
      </FormSection>

      <FormSection title="Doctor" data-id="element-194">
        {isOwnOnly && linkedDoctor && !isEdit ? <div className="bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800 rounded-lg p-4" data-id="element-195">
            <p className="text-sm text-sky-700 dark:text-sky-400 font-medium" data-id="element-196">
              Doctor asignado automáticamente
            </p>
            <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1" data-id="element-197">
              {getDoctorPrefix(linkedDoctor.gender, getSpecialty(linkedDoctor.specialtyId)?.name)}{' '}
              {linkedDoctor.name}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-198">
              {getSpecialty(linkedDoctor.specialtyId)?.name || ''}
            </p>
          </div> : <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-199">
            <Select label="Especialidad (opcional)" options={specialtyOptions} value={selectedSpecialty} onChange={value => {
          setSelectedSpecialty(value);
          setFormData({
            ...formData,
            doctorId: ''
          });
        }} data-id="element-200" />
        
            <Select label="Doctor" options={doctorOptions} value={formData.doctorId} onChange={value => {
          if (value) {
            const selectedDoc = doctors.find(d => d.id === value);
            if (selectedDoc) setSelectedSpecialty(selectedDoc.specialtyId);
          }
          setFormData({
            ...formData,
            doctorId: value
          });
        }} error={formErrors.doctorId} searchable placeholder="Seleccionar doctor..." data-id="element-201" />
        
          </div>}
      </FormSection>

      <FormSection title="Servicios" data-id="element-202">
        <div className="space-y-3" data-id="element-203">
          <div className="relative" data-id="element-204">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-205" />
            <input type="text" placeholder="Buscar servicios..." value={serviceSearchTerm} onChange={e => setServiceSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all" data-id="element-206" />
          
          </div>
          <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-2 bg-gray-50 dark:bg-gray-900/50" data-id="element-207">
            {filteredFormServices.length === 0 ? <p className="text-sm text-gray-500 italic p-2" data-id="element-208">
                {serviceSearchTerm ? 'No se encontraron servicios con ese nombre' : 'No hay servicios activos disponibles'}
              </p> : <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-209">
                {filteredFormServices.map(service => <label key={service.id} className={`
                      flex items-center gap-3 p-2 rounded-md cursor-pointer transition-colors
                      ${formData.serviceIds.includes(service.id) ? 'bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800' : 'hover:bg-white dark:hover:bg-gray-800 border border-transparent'}
                    `} data-id="element-210">
              
                    <input type="checkbox" checked={formData.serviceIds.includes(service.id)} onChange={() => toggleService(service.id)} className="w-4 h-4 text-sky-600 rounded border-gray-300 focus:ring-sky-500" data-id="element-211" />
              
                    <div className="flex-1 min-w-0" data-id="element-212">
                      <span className="text-sm font-medium text-gray-900 dark:text-white truncate block" data-id="element-213">
                        {service.name}
                      </span>
                      <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-214">
                        {service.duration} min
                      </p>
                    </div>
                  </label>)}
              </div>}
          </div>
          {formErrors.serviceId && <p className="text-sm text-red-500" data-id="element-215">{formErrors.serviceId}</p>}
          {formData.serviceIds.length > 0 && <div className="flex flex-wrap gap-2 mt-2" data-id="element-216">
              {selectedServicesList.map(s => <span key={s.id} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-200" data-id="element-217">
            
                  {s.name}
                  <button type="button" onClick={() => toggleService(s.id)} className="ml-1 hover:text-sky-900 dark:hover:text-sky-100" data-id="element-218">
              
                    <XIcon className="w-3 h-3" data-id="element-219" />
                  </button>
                </span>)}
            </div>}
          <div className="flex items-center gap-2 p-3 bg-gray-100 dark:bg-gray-800 rounded-lg" data-id="element-220">
            <ClockIcon className="w-4 h-4 text-gray-500" data-id="element-221" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-222">
              Duración Total: {totalDuration} min
            </span>
          </div>
        </div>
      </FormSection>

      <FormSection title="Fecha y Hora" data-id="element-223">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-224">
          <Input type="date" label="Fecha" value={formData.date} onChange={e => setFormData({
          ...formData,
          date: e.target.value
        })} error={formErrors.date} data-id="element-225" />
        
          <Input type="time" label="Hora" value={formData.time} onChange={e => setFormData({
          ...formData,
          time: e.target.value
        })} error={formErrors.time} data-id="element-226" />
        
        </div>
      </FormSection>

      <FormSection title="Notas" data-id="element-227">
        <Textarea label="Notas Adicionales" value={formData.notes} onChange={e => setFormData({
        ...formData,
        notes: e.target.value
      })} rows={3} placeholder="Detalles adicionales sobre la cita..." data-id="element-228" />
      
      </FormSection>

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-800" data-id="element-229">
        <Button variant="outline" type="button" onClick={() => isEdit ? setIsEditOpen(false) : setIsFormOpen(false)} data-id="element-230">
        
          Cancelar
        </Button>
        <Button type="submit" data-id="element-231">
          {isEdit ? 'Guardar Cambios' : 'Crear Cita'}
        </Button>
      </div>
    </div>;
  useEffect(() => {
    if (initialDateFilter) setFilterDate(initialDateFilter);
  }, [initialDateFilter]);
  return <div className="space-y-6" data-id="element-232">
      {/* Header Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-sky-600 via-sky-500 to-cyan-500 p-6 text-white shadow-xl" data-id="element-233">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-234" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4 w-full" data-id="element-235">
          <div data-id="element-236">
            <div className="flex items-center gap-3 mb-2" data-id="element-237">
              <div className="p-2.5 bg-white/15 backdrop-blur-sm rounded-xl" data-id="element-238">
                <CalendarIcon className="w-6 h-6" data-id="element-239" />
              </div>
              <h1 className="text-2xl font-bold" data-id="element-240">Citas</h1>
            </div>
            <p className="text-sky-100 max-w-xl" data-id="element-241">
              {isOwnOnly && linkedDoctor ? `Citas de ${getDoctorPrefix(linkedDoctor.gender, getSpecialty(linkedDoctor.specialtyId)?.name)} ${linkedDoctor.name}` : 'Gestión de citas médicas'}
              {isReadOnlyAppointments && <span className="ml-2 bg-white/20 px-2 py-0.5 rounded text-xs font-medium" data-id="element-242">
                  Solo lectura
                </span>}
            </p>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-243">
            {canExportPdf && <Button variant="outline" leftIcon={<ClockIcon className="w-4 h-4" data-id="element-245" />} onClick={() => setIsPdfExportModalOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-244">
              
                Exportar PDF
              </Button>}
            {canExportExcel && <Button variant="outline" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-247" />} onClick={() => setIsExportModalOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-246">
              
                Exportar Excel
              </Button>}
            {canCreateAppointments && <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-249" />} onClick={openNewForm} className="bg-white text-sky-700 hover:bg-sky-50 shadow-lg shadow-sky-900/20" data-id="element-248">
              
                Nueva Cita
              </Button>}
          </div>
        </div>
      </div>

      {isReadOnlyAppointments && <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-700 dark:text-amber-300 text-sm flex items-center gap-2" data-id="element-250">
          <LockIcon className="w-4 h-4" data-id="element-251" />
          <span data-id="element-252">
            Solo tienes permisos de lectura. No puedes crear, editar o cancelar
            citas.
          </span>
        </div>}

      {hasOwnButNoDoctor && <Card className="bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800" data-id="element-253">
          <div className="flex items-center gap-3 p-1" data-id="element-254">
            <div className="w-10 h-10 rounded-lg bg-amber-500 flex items-center justify-center flex-shrink-0" data-id="element-255">
              <ClockIcon className="w-5 h-5 text-white" data-id="element-256" />
            </div>
            <div data-id="element-257">
              <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-300" data-id="element-258">
                Doctor no vinculado
              </h3>
              <p className="text-sm text-amber-700 dark:text-amber-400" data-id="element-259">
                Tu cuenta ({user?.email}) no está vinculada a ningún registro de
                doctor. Contacta al administrador para que el email de tu
                usuario coincida con el email de tu registro de doctor.
              </p>
            </div>
          </div>
        </Card>}

      {/* View Toggle and Filters */}
      <Card data-id="element-260">
        <div className="flex flex-col gap-4" data-id="element-261">
          <div className="flex flex-col gap-4" data-id="element-262">
            <div className="flex flex-wrap gap-2" data-id="element-263">
              {viewTabs.map(tab => <button key={tab.id} onClick={() => setActiveView(tab.id as 'list' | 'calendar')} className={`
                    flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors
                    ${activeView === tab.id ? 'bg-sky-100 text-sky-600 dark:bg-sky-900/30 dark:text-sky-400' : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'}
                  `} data-id="element-264">
                
                  {tab.icon}
                  {tab.label}
                </button>)}
            </div>
            <div className="flex-1 flex flex-col sm:flex-row gap-4" data-id="element-265">
              <div className="flex-1 relative" data-id="element-266">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-267" />
                <Input placeholder="Buscar por paciente o doctor..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-id="element-268" />
                
              </div>
              <div className="w-full sm:w-48" data-id="element-269">
                <Select options={statusOptions} value={filterStatus} onChange={setFilterStatus} placeholder="Estado" data-id="element-270" />
                
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-gray-100 dark:border-gray-700/50" data-id="element-271">
            <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mr-1" data-id="element-272">
              Fecha:
            </span>
            <button onClick={() => setFilterDate('')} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-150 ${!filterDate ? 'bg-sky-500 text-white shadow-sm shadow-sky-500/30' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`} data-id="element-273">
              
              Todas
            </button>
            <button onClick={() => setFilterDate(today)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-150 ${filterDate === today ? 'bg-sky-500 text-white shadow-sm shadow-sky-500/30' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`} data-id="element-274">
              
              <CalendarIcon className="w-3.5 h-3.5" data-id="element-275" />
              Hoy
            </button>
            <button onClick={() => setFilterDate(getTomorrow())} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-150 ${filterDate === getTomorrow() ? 'bg-sky-500 text-white shadow-sm shadow-sky-500/30' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`} data-id="element-276">
              
              Mañana
            </button>
            <div className="relative" ref={pickerRef} data-id="element-277">
              <button type="button" onClick={() => {
              setPickerNavDate(filterDate ? new Date(filterDate + 'T12:00:00') : new Date());
              setIsDatePickerOpen(v => !v);
            }} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-150 ${filterDate && filterDate !== today && filterDate !== getTomorrow() ? 'bg-sky-500 text-white shadow-sm shadow-sky-500/30' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`} data-id="element-278">
                
                <CalendarIcon className="w-3.5 h-3.5" data-id="element-279" />
                {filterDate && filterDate !== today && filterDate !== getTomorrow() ? (() => {
                const [y, m, d] = filterDate.split('-');
                return `${d}/${m}/${y}`;
              })() : 'Elegir fecha'}
              </button>
              {isDatePickerOpen && (() => {
              const pYear = pickerNavDate.getFullYear();
              const pMonth = pickerNavDate.getMonth();
              const pDays = getDaysInMonth(pYear, pMonth);
              const pFirstDay = getFirstDayOfMonth(pYear, pMonth);
              const pCells: (number | null)[] = [];
              for (let i = 0; i < pFirstDay; i++) pCells.push(null);
              for (let i = 1; i <= pDays; i++) pCells.push(i);
              return <div className="absolute top-full left-0 mt-2 z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl p-3 w-64" data-id="element-280">
                      <div className="flex items-center justify-between mb-2" data-id="element-281">
                        <button type="button" onClick={() => setPickerNavDate(new Date(pYear, pMonth - 1, 1))} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors" data-id="element-282">
                        
                          <ChevronLeftIcon className="w-4 h-4 text-gray-500" data-id="element-283" />
                        </button>
                        <span className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-284">
                          {getMonthName(pMonth)} {pYear}
                        </span>
                        <button type="button" onClick={() => setPickerNavDate(new Date(pYear, pMonth + 1, 1))} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors" data-id="element-285">
                        
                          <ChevronRightIcon className="w-4 h-4 text-gray-500" data-id="element-286" />
                        </button>
                      </div>
                      <div className="grid grid-cols-7 mb-1" data-id="element-287">
                        {['D', 'L', 'M', 'X', 'J', 'V', 'S'].map(d => <div key={d} className="text-center text-[10px] font-semibold text-gray-400 py-1" data-id="element-288">
                        
                            {d}
                          </div>)}
                      </div>
                      <div className="grid grid-cols-7 gap-0.5" data-id="element-289">
                        {pCells.map((day, idx) => {
                    if (day === null) return <div key={`e-${idx}`} data-id="element-290" />;
                    const dateStr = `${pYear}-${String(pMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                    const isToday = dateStr === today;
                    const isSelected = dateStr === filterDate;
                    return <button key={day} type="button" onClick={() => {
                      setFilterDate(dateStr);
                      setIsDatePickerOpen(false);
                    }} className={`
                                aspect-square rounded-lg text-xs font-medium flex items-center justify-center transition-colors
                                ${isSelected ? 'bg-sky-500 text-white' : ''}
                                ${isToday && !isSelected ? 'bg-sky-100 dark:bg-sky-900/40 text-sky-600 dark:text-sky-400 font-bold' : ''}
                                ${!isSelected && !isToday ? 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300' : ''}
                              `} data-id="element-291">
                            
                              {day}
                            </button>;
                  })}
                      </div>
                      <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700 flex justify-between items-center" data-id="element-292">
                        <button type="button" onClick={() => {
                    setFilterDate(today);
                    setIsDatePickerOpen(false);
                  }} className="text-xs text-sky-600 dark:text-sky-400 hover:underline font-medium" data-id="element-293">
                        
                          Ir a hoy
                        </button>
                        <button type="button" onClick={() => setIsDatePickerOpen(false)} className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" data-id="element-294">
                        
                          Cerrar
                        </button>
                      </div>
                    </div>;
            })()}
            </div>
            {filterDate && <button onClick={() => setFilterDate('')} className="inline-flex items-center gap-1 px-2 py-1.5 rounded-full text-xs font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors" title="Limpiar filtro de fecha" data-id="element-295">
              
                <XIcon className="w-3.5 h-3.5" data-id="element-296" />
                Limpiar
              </button>}
            <span className="ml-auto text-xs text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 px-2.5 py-1 rounded-full" data-id="element-297">
              {filteredAppointments.length} cita
              {filteredAppointments.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </Card>

      {/* List View */}
      {activeView === 'list' && <Card padding="none" data-id="element-298">
          {/* ── MOBILE CARD LIST (hidden on sm+) ── */}
          <div className="sm:hidden divide-y divide-gray-100 dark:divide-gray-800" data-id="element-299">
            {filteredAppointments.length === 0 ? <div className="py-12 text-center text-gray-500 dark:text-gray-400" data-id="element-300">
                No se encontraron citas
              </div> : paginatedAppointments.map(apt => {
          const patient = getPatient(apt.patientId);
          const doctor = getDoctor(apt.doctorId);
          const serviceIds = apt.serviceIds && apt.serviceIds.length > 0 ? apt.serviceIds : [apt.serviceId];
          const aptServices = services.filter(s => serviceIds.includes(s.id));
          const serviceNames = aptServices.map(s => s.name).join(', ');
          const canModify = apt.status === 'programada' || apt.status === 'reprogramada';
          return <div key={apt.id} className="px-4 py-3 flex items-start justify-between gap-3" data-id="element-301">
                  <div className="flex-1 min-w-0" data-id="element-302">
                    <p className="font-semibold text-gray-900 dark:text-white text-sm truncate" data-id="element-303">
                      {patient?.name || '-'}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5" data-id="element-304">
                      {doctor ? `${getDoctorPrefix(doctor.gender, getSpecialty(doctor.specialtyId)?.name)} ${doctor.name}` : '-'}
                    </p>
                    {serviceNames && <p className="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5" data-id="element-305">{serviceNames}</p>}
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap" data-id="element-306">
                      <span className="text-xs text-sky-600 dark:text-sky-400 font-medium" data-id="element-307">{apt.date} · {formatTime(apt.time)}</span>
                      <Badge variant={getStatusBadgeVariant(apt.status)} className="text-xs py-0 px-1.5" data-id="element-308">
                        {getStatusName(apt.status)}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0" data-id="element-309">
                    {canModify && <>
                      {!isReadOnlyAppointments && <button onClick={() => openEditModal(apt)} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors" title="Editar" data-id="element-310"><EditIcon className="w-4 h-4" data-id="element-311" /></button>}
                      <button onClick={() => openActionSelectModal(apt)} className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors" title="Gestionar" data-id="element-312"><CheckIcon className="w-4 h-4" data-id="element-313" /></button>
                    </>}
                  </div>
                </div>;
        })}
          </div>

          {/* ── DESKTOP TABLE (hidden on mobile) ── */}
          <div className="hidden sm:block overflow-x-auto" data-id="element-314">
            <table className="w-full" data-id="element-315">
              <thead data-id="element-316">
                <tr className="border-b border-gray-200 dark:border-gray-700" data-id="element-317">
                  <SortableHeader label="Paciente" sortKey="patientId" currentDirection={aptIsSorted('patientId')} onSort={aptRequestSort} data-id="element-318" />
                  <SortableHeader label="Doctor" sortKey="doctorId" currentDirection={aptIsSorted('doctorId')} onSort={aptRequestSort} data-id="element-319" />
                  <SortableHeader label="Servicios" sortKey="serviceId" currentDirection={aptIsSorted('serviceId')} onSort={aptRequestSort} className="hidden md:table-cell" data-id="element-320" />
                  <SortableHeader label="Fecha y Hora" sortKey="date" currentDirection={aptIsSorted('date')} onSort={aptRequestSort} data-id="element-321" />
                  <SortableHeader label="Estado" sortKey="status" currentDirection={aptIsSorted('status')} onSort={aptRequestSort} data-id="element-322" />
                  <SortableHeader label="Creada por" sortKey="createdByUserName" currentDirection={aptIsSorted('createdByUserName')} onSort={aptRequestSort} className="hidden md:table-cell" data-id="element-323" />
                  <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-324">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-325">
                {filteredAppointments.length === 0 ? <tr data-id="element-326"><td colSpan={7} className="py-12 text-center text-gray-500 dark:text-gray-400" data-id="element-327">No se encontraron citas</td></tr> : paginatedAppointments.map(apt => {
              const patient = getPatient(apt.patientId);
              const doctor = getDoctor(apt.doctorId);
              const serviceIds = apt.serviceIds && apt.serviceIds.length > 0 ? apt.serviceIds : [apt.serviceId];
              const aptServices = services.filter(s => serviceIds.includes(s.id));
              const serviceNames = aptServices.map(s => s.name).join(', ');
              const canModify = apt.status === 'programada' || apt.status === 'reprogramada';
              return <tr key={apt.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50" data-id="element-328">
                      <td className="py-4 px-6" data-id="element-329"><p className="font-medium text-gray-900 dark:text-white" data-id="element-330">{patient?.name || '-'}</p></td>
                      <td className="py-4 px-6" data-id="element-331">
                        <p className="text-gray-900 dark:text-gray-100" data-id="element-332">{doctor ? `${getDoctorPrefix(doctor.gender, getSpecialty(doctor.specialtyId)?.name)} ${doctor.name}` : ''}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-333">{doctor && getSpecialty(doctor.specialtyId)?.name}</p>
                      </td>
                      <td className="py-4 px-6 hidden md:table-cell" data-id="element-334">
                        <p className="text-gray-900 dark:text-gray-100 line-clamp-2" data-id="element-335">{serviceNames || '-'}</p>
                        <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded" data-id="element-336">{apt.duration} min</span>
                      </td>
                      <td className="py-4 px-6" data-id="element-337">
                        <p className="text-gray-900 dark:text-gray-100" data-id="element-338">{apt.date}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-339">{formatTime(apt.time)}</p>
                      </td>
                      <td className="py-4 px-6" data-id="element-340"><Badge variant={getStatusBadgeVariant(apt.status)} data-id="element-341">{getStatusName(apt.status)}</Badge></td>
                      <td className="py-4 px-6 hidden md:table-cell" data-id="element-342">
                        {apt.createdByUserName ? <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-343">{apt.createdByUserName}</span> : <span className="text-sm text-gray-400 italic" data-id="element-344">—</span>}
                      </td>
                      <td className="py-4 px-6" data-id="element-345">
                        <div className="flex items-center justify-end gap-1" data-id="element-346">
                          {canModify && <>
                            {!isReadOnlyAppointments && <button onClick={() => openEditModal(apt)} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors" title="Editar" data-id="element-347"><EditIcon className="w-4 h-4" data-id="element-348" /></button>}
                            <button onClick={() => openActionSelectModal(apt)} className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors" title="Gestionar cita" data-id="element-349"><CheckIcon className="w-4 h-4" data-id="element-350" /></button>
                          </>}
                        </div>
                      </td>
                    </tr>;
            })}
              </tbody>
            </table>
          </div>

          <div className="px-4 pb-4" data-id="element-351">
            <Pagination currentPage={aptPage} totalPages={aptTotalPages} totalItems={aptTotalItems} startIndex={aptStartIndex} endIndex={aptEndIndex} onPageChange={aptGoToPage} onNext={aptNextPage} onPrev={aptPrevPage} data-id="element-352" />
          </div>
        </Card>}

      {/* Calendar View */}
      {activeView === 'calendar' && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-353">
          <Card className="lg:col-span-2" data-id="element-354">
            <CardHeader data-id="element-355">
              <div className="flex items-center justify-between" data-id="element-356">
                <CardTitle data-id="element-357">Calendario de Citas</CardTitle>
                <div className="flex items-center gap-2" data-id="element-358">
                  <button onClick={prevMonth} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg" data-id="element-359">
                  
                    <ChevronLeftIcon className="w-5 h-5 text-gray-500" data-id="element-360" />
                  </button>
                  <span className="text-base sm:text-lg font-medium text-gray-900 dark:text-white min-w-[120px] sm:min-w-[160px] text-center" data-id="element-361">
                    {getMonthName(month)} {year}
                  </span>
                  <button onClick={nextMonth} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg" data-id="element-362">
                  
                    <ChevronRightIcon className="w-5 h-5 text-gray-500" data-id="element-363" />
                  </button>
                </div>
              </div>
            </CardHeader>
            <CardContent data-id="element-364">
              <div className="grid grid-cols-7 gap-1 mb-2" data-id="element-365">
                {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map(day => <div key={day} className="text-center text-sm font-medium text-gray-500 dark:text-gray-400 py-2" data-id="element-366">
                  
                      {day}
                    </div>)}
              </div>
              <div className="grid grid-cols-7 gap-1" data-id="element-367">
                {calendarDays.map((day, index) => {
              if (day === null) return <div key={`empty-${index}`} className="aspect-square" data-id="element-368" />;
              const dateStr = getDateString(day);
              const appointmentCount = getAppointmentCountForDay(day);
              const isToday = dateStr === today;
              const isSelected = dateStr === selectedDate;
              return <button key={day} onClick={() => setSelectedDate(dateStr)} className={`
                        aspect-square rounded-lg text-sm p-1 flex flex-col items-center justify-center transition-colors
                        ${isToday ? 'bg-sky-500 text-white' : ''}
                        ${isSelected && !isToday ? 'bg-sky-100 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400 ring-2 ring-sky-500' : ''}
                        ${!isToday && !isSelected ? 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300' : ''}
                      `} data-id="element-369">
                    
                      <span className="font-medium" data-id="element-370">{day}</span>
                      {appointmentCount > 0 && <span className={`text-[10px] px-1.5 py-0.5 rounded-full mt-0.5 ${isToday ? 'bg-sky-400 text-white' : 'bg-sky-100 text-sky-600 dark:bg-sky-900 dark:text-sky-400'}`} data-id="element-371">
                      
                          {appointmentCount}
                        </span>}
                    </button>;
            })}
              </div>
            </CardContent>
          </Card>

          <Card data-id="element-372">
            <CardHeader data-id="element-373">
              <CardTitle data-id="element-374">
                {selectedDate === today ? 'Citas de Hoy' : `Citas del ${selectedDate}`}
              </CardTitle>
            </CardHeader>
            <CardContent data-id="element-375">
              {(() => {
            const visibleDateAppointments = selectedDateAppointments.filter(apt => {
              if (isOwnOnly && linkedDoctor) return apt.doctorId === linkedDoctor.id;
              return true;
            });
            return visibleDateAppointments.length === 0 ? <p className="text-center text-gray-500 dark:text-gray-400 py-8" data-id="element-376">
                    No hay citas programadas
                  </p> : <div className="space-y-3" data-id="element-377">
                    {visibleDateAppointments.map(apt => {
                const patient = getPatient(apt.patientId);
                const doctor = getDoctor(apt.doctorId);
                const serviceIds = apt.serviceIds && apt.serviceIds.length > 0 ? apt.serviceIds : [apt.serviceId];
                const aptServices = services.filter(s => serviceIds.includes(s.id));
                const serviceNames = aptServices.map(s => s.name).join(', ');
                return <div key={apt.id} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg" data-id="element-378">
                      
                          <div className="flex items-start justify-between mb-2" data-id="element-379">
                            <div data-id="element-380">
                              <p className="font-medium text-gray-900 dark:text-white" data-id="element-381">
                                {formatTime(apt.time)}
                              </p>
                              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-382">
                                {apt.duration} min
                              </p>
                            </div>
                            <Badge variant={getStatusBadgeVariant(apt.status)} data-id="element-383">
                              {getStatusName(apt.status)}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-900 dark:text-gray-100" data-id="element-384">
                            {patient?.name}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-385">
                            Dr. {doctor?.name}
                          </p>
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 line-clamp-1" title={serviceNames} data-id="element-386">
                        
                            {serviceNames}
                          </p>
                        </div>;
              })}
                  </div>;
          })()}
            </CardContent>
          </Card>
        </div>}

      {/* ═══════════════════════════════════════════════════════════════════════
                 NEW: Action Select Modal — aparece al presionar ✓ en cualquier cita
              ════════════════════════════════════════════════════════════════════════ */}
      <Modal isOpen={isActionSelectOpen} onClose={() => setIsActionSelectOpen(false)} title="Gestionar Cita" size="sm" icon={<CalendarIcon className="w-6 h-6" data-id="element-388" />} accentColor="sky" data-id="element-387">
        
        <div className="space-y-4" data-id="element-389">
          {/* Appointment summary */}
          {selectedAppointment && (() => {
          const pt = getPatient(selectedAppointment.patientId);
          const doc = getDoctor(selectedAppointment.doctorId);
          const sIds = selectedAppointment.serviceIds && selectedAppointment.serviceIds.length > 0 ? selectedAppointment.serviceIds : [selectedAppointment.serviceId];
          const aptSvcs = services.filter(s => sIds.includes(s.id));
          const svcNames = aptSvcs.map(s => s.name).join(', ');
          return <div className="flex items-center gap-3 p-3.5 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-100 dark:border-gray-700" data-id="element-390">
                  <div className="w-10 h-10 rounded-full bg-sky-100 dark:bg-sky-900/40 flex items-center justify-center flex-shrink-0" data-id="element-391">
                    <UserIcon className="w-5 h-5 text-sky-600 dark:text-sky-400" data-id="element-392" />
                  </div>
                  <div className="min-w-0 flex-1" data-id="element-393">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white truncate" data-id="element-394">
                      {pt?.name || '-'}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate" data-id="element-395">
                      {doc ? `${getDoctorPrefix(doc.gender, getSpecialty(doc.specialtyId)?.name)} ${doc.name}` : '-'}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5" data-id="element-396">
                      {selectedAppointment.date} ·{' '}
                      {formatTime(selectedAppointment.time)}
                      {svcNames ? ` · ${svcNames}` : ''}
                    </p>
                  </div>
                </div>;
        })()}

          {/* Question label */}
          <p className="text-sm text-center text-gray-500 dark:text-gray-400 font-medium" data-id="element-397">
            ¿Qué deseas hacer con esta cita?
          </p>

          {/* Action cards */}
          <div className="space-y-2.5" data-id="element-398">
            {/* Complete */}
            <button type="button" onClick={() => {
            setIsActionSelectOpen(false);
            if (selectedAppointment) openCompleteModal(selectedAppointment);
          }} className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-transparent bg-gray-50 dark:bg-gray-700/40 hover:border-green-400 dark:hover:border-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 transition-all group text-left" data-id="element-399">
              
              <div className="w-11 h-11 rounded-xl bg-green-100 dark:bg-green-900/50 flex items-center justify-center flex-shrink-0 group-hover:bg-green-200 dark:group-hover:bg-green-900/70 transition-colors" data-id="element-400">
                <CheckIcon className="w-5 h-5 text-green-700 dark:text-green-400" data-id="element-401" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-402">
                <p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-403">
                  Completar cita
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-404">
                  Marcar como completada y programar próxima cita
                </p>
              </div>
              <ChevronRightIcon className="w-4 h-4 text-gray-300 dark:text-gray-600 group-hover:text-green-500 dark:group-hover:text-green-400 transition-colors flex-shrink-0" data-id="element-405" />
            </button>

            {/* Reschedule */}
            <button type="button" onClick={() => {
            setIsActionSelectOpen(false);
            if (selectedAppointment) openRescheduleModal(selectedAppointment);
          }} className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-transparent bg-gray-50 dark:bg-gray-700/40 hover:border-sky-400 dark:hover:border-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/20 transition-all group text-left" data-id="element-406">
              
              <div className="w-11 h-11 rounded-xl bg-sky-100 dark:bg-sky-900/50 flex items-center justify-center flex-shrink-0 group-hover:bg-sky-200 dark:group-hover:bg-sky-900/70 transition-colors" data-id="element-407">
                <RefreshCwIcon className="w-5 h-5 text-sky-700 dark:text-sky-400" data-id="element-408" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-409">
                <p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-410">
                  Reprogramar cita
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-411">
                  Cambiar la fecha y hora de la cita
                </p>
              </div>
              <ChevronRightIcon className="w-4 h-4 text-gray-300 dark:text-gray-600 group-hover:text-sky-500 dark:group-hover:text-sky-400 transition-colors flex-shrink-0" data-id="element-412" />
            </button>

            {/* Cancel */}
            <button type="button" onClick={() => {
            setIsActionSelectOpen(false);
            if (selectedAppointment) openCancelModal(selectedAppointment);
          }} className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-transparent bg-gray-50 dark:bg-gray-700/40 hover:border-red-400 dark:hover:border-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all group text-left" data-id="element-413">
              
              <div className="w-11 h-11 rounded-xl bg-red-100 dark:bg-red-900/50 flex items-center justify-center flex-shrink-0 group-hover:bg-red-200 dark:group-hover:bg-red-900/70 transition-colors" data-id="element-414">
                <XIcon className="w-5 h-5 text-red-700 dark:text-red-400" data-id="element-415" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-416">
                <p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-417">
                  Cancelar cita
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-418">
                  Cancelar y notificar al paciente y al doctor
                </p>
              </div>
              <ChevronRightIcon className="w-4 h-4 text-gray-300 dark:text-gray-600 group-hover:text-red-500 dark:group-hover:text-red-400 transition-colors flex-shrink-0" data-id="element-419" />
            </button>
          </div>

          {/* Back button */}
          <button type="button" onClick={() => setIsActionSelectOpen(false)} className="w-full py-2.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-all" data-id="element-420">
            
            Volver
          </button>
        </div>
      </Modal>
      {/* ════════════════════════════════════════════════════════════════════ */}

      {/* New Appointment Modal */}
      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title="Nueva Cita" size="lg" icon={<CalendarIcon className="w-6 h-6" data-id="element-422" />} accentColor="sky" data-id="element-421">
        
        <form onSubmit={handleSubmit} data-id="element-423">{renderAppointmentForm(false)}</form>
      </Modal>

      {/* Edit Appointment Modal */}
      <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} title="Editar Cita" size="lg" icon={<EditIcon className="w-6 h-6" data-id="element-425" />} accentColor="blue" data-id="element-424">
        
        <form onSubmit={handleEditSubmit} data-id="element-426">{renderAppointmentForm(true)}</form>
      </Modal>

      {/* Cancel Modal */}
      <Modal isOpen={isCancelOpen} onClose={() => setIsCancelOpen(false)} title="Cancelar Cita" size="md" data-id="element-427">
        
        <div className="space-y-4" data-id="element-428">
          <p className="text-gray-600 dark:text-gray-400" data-id="element-429">
            ¿Está seguro de que desea cancelar esta cita? Se notificará al
            paciente y al doctor.
          </p>
          <Textarea label="Motivo de cancelación" value={cancelReason} onChange={e => setCancelReason(e.target.value)} placeholder="Ingrese el motivo de la cancelación..." rows={3} data-id="element-430" />
          
          <div className="flex justify-end gap-3" data-id="element-431">
            <Button variant="outline" onClick={() => setIsCancelOpen(false)} data-id="element-432">
              Volver
            </Button>
            <Button variant="danger" onClick={handleCancel} data-id="element-433">
              Cancelar Cita
            </Button>
          </div>
        </div>
      </Modal>

      {/* Reschedule Modal */}
      <Modal isOpen={isRescheduleOpen} onClose={() => setIsRescheduleOpen(false)} title="Reprogramar Cita" size="md" data-id="element-434">
        
        <div className="space-y-4" data-id="element-435">
          <p className="text-gray-600 dark:text-gray-400" data-id="element-436">
            Seleccione la nueva fecha y hora para la cita.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-437">
            <Input type="date" label="Nueva Fecha" value={rescheduleData.date} onChange={e => setRescheduleData({
            ...rescheduleData,
            date: e.target.value
          })} data-id="element-438" />
            
            <Input type="time" label="Nueva Hora" value={rescheduleData.time} onChange={e => setRescheduleData({
            ...rescheduleData,
            time: e.target.value
          })} data-id="element-439" />
            
          </div>
          <div className="flex justify-end gap-3" data-id="element-440">
            <Button variant="outline" onClick={() => setIsRescheduleOpen(false)} data-id="element-441">
              
              Cancelar
            </Button>
            <Button onClick={handleReschedule} data-id="element-442">Reprogramar</Button>
          </div>
        </div>
      </Modal>

      {/* Complete Appointment Modal */}
      <Modal isOpen={isCompleteOpen} onClose={() => setIsCompleteOpen(false)} title="Completar Cita" size="lg" data-id="element-443">
        
        <div className="space-y-4" data-id="element-444">
          {selectedAppointment && <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4" data-id="element-445">
              <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-446">
                <div data-id="element-447">
                  <span className="text-gray-500 dark:text-gray-400" data-id="element-448">
                    Paciente:
                  </span>
                  <p className="font-medium text-gray-900 dark:text-white" data-id="element-449">
                    {getPatient(selectedAppointment.patientId)?.name}
                  </p>
                </div>
                <div data-id="element-450">
                  <span className="text-gray-500 dark:text-gray-400" data-id="element-451">
                    Doctor:
                  </span>
                  <p className="font-medium text-gray-900 dark:text-white" data-id="element-452">
                    {(() => {
                  const doc = getDoctor(selectedAppointment.doctorId);
                  return doc ? `${getDoctorPrefix(doc.gender, getSpecialty(doc.specialtyId)?.name)} ${doc.name}` : '-';
                })()}
                  </p>
                </div>
                <div data-id="element-453">
                  <span className="text-gray-500 dark:text-gray-400" data-id="element-454">
                    Fecha:
                  </span>
                  <p className="font-medium text-gray-900 dark:text-white" data-id="element-455">
                    {selectedAppointment.date} -{' '}
                    {formatTime(selectedAppointment.time)}
                  </p>
                </div>
                <div data-id="element-456">
                  <span className="text-gray-500 dark:text-gray-400" data-id="element-457">
                    Servicios:
                  </span>
                  <p className="font-medium text-gray-900 dark:text-white" data-id="element-458">
                    {(() => {
                  const sIds = selectedAppointment.serviceIds && selectedAppointment.serviceIds.length > 0 ? selectedAppointment.serviceIds : [selectedAppointment.serviceId];
                  const aptServices = services.filter(s => sIds.includes(s.id));
                  return aptServices.map(s => s.name).join(', ') || '-';
                })()}
                  </p>
                </div>
                {selectedAppointment.createdByUserName && <div className="col-span-2" data-id="element-459">
                    <span className="text-gray-500 dark:text-gray-400" data-id="element-460">
                      Creada por:
                    </span>
                    <p className="font-medium text-gray-900 dark:text-white flex items-center gap-1.5" data-id="element-461">
                      <UserIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-462" />
                      {selectedAppointment.createdByUserName}
                    </p>
                  </div>}
              </div>
            </div>}

          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4" data-id="element-463">
            <p className="text-sm text-green-800 dark:text-green-300" data-id="element-464">
              ¿Confirmar que esta cita fue completada exitosamente?
            </p>
          </div>

          <div className="border border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4" data-id="element-465">
            <div className="flex items-center gap-2 mb-4" data-id="element-466">
              <CalendarIcon className="w-4 h-4 text-gray-400" data-id="element-467" />
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-468">
                Próxima Cita Sugerida
              </h4>
              <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 px-2 py-0.5 rounded-full" data-id="element-469">
                Opcional
              </span>
            </div>
            <div className="space-y-4" data-id="element-470">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-471">
                <Input type="date" label="Fecha Sugerida" value={completeData.nextAppointmentDate} onChange={e => setCompleteData({
                ...completeData,
                nextAppointmentDate: e.target.value
              })} min={getToday()} data-id="element-472" />
                
                <Input type="time" label="Hora Sugerida" value={completeData.nextAppointmentTime} onChange={e => setCompleteData({
                ...completeData,
                nextAppointmentTime: e.target.value
              })} data-id="element-473" />
                
              </div>
              <div data-id="element-474">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-475">
                  Servicios para la Próxima Cita
                </label>
                {completeData.nextServiceIds.length > 0 && <div className="flex flex-wrap gap-2 mb-3" data-id="element-476">
                    {completeData.nextServiceIds.map(sId => {
                  const svc = getService(sId);
                  if (!svc) return null;
                  return <span key={sId} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-200" data-id="element-477">
                        
                          {svc.name}
                          <button type="button" onClick={() => setCompleteData(prev => ({
                      ...prev,
                      nextServiceIds: prev.nextServiceIds.filter(id => id !== sId)
                    }))} className="ml-1 hover:text-sky-900 dark:hover:text-sky-100" data-id="element-478">
                          
                            <XIcon className="w-3 h-3" data-id="element-479" />
                          </button>
                        </span>;
                })}
                  </div>}
                <div className="relative mb-2" data-id="element-480">
                  <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-481" />
                  <input type="text" placeholder="Buscar servicios para agregar..." value={serviceSearchTerm} onChange={e => setServiceSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all" data-id="element-482" />
                  
                </div>
                <div className="max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900/50" data-id="element-483">
                  {availableServices.filter(s => serviceSearchTerm ? s.name.toLowerCase().includes(serviceSearchTerm.toLowerCase()) : true).map(service => {
                  const isSelected = completeData.nextServiceIds.includes(service.id);
                  return <label key={service.id} className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-800 last:border-0 ${isSelected ? 'bg-sky-50 dark:bg-sky-900/20' : 'hover:bg-white dark:hover:bg-gray-800'}`} data-id="element-484">
                        
                          <input type="checkbox" checked={isSelected} onChange={() => setCompleteData(prev => ({
                      ...prev,
                      nextServiceIds: isSelected ? prev.nextServiceIds.filter(id => id !== service.id) : [...prev.nextServiceIds, service.id]
                    }))} className="w-4 h-4 text-sky-600 rounded border-gray-300 focus:ring-sky-500" data-id="element-485" />
                        
                          <div className="flex-1 min-w-0" data-id="element-486">
                            <span className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-487">
                              {service.name}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400 ml-2" data-id="element-488">
                              {service.duration} min
                            </span>
                          </div>
                        </label>;
                })}
                </div>
              </div>
              <Textarea label="Notas para la Próxima Cita" value={completeData.nextAppointmentNotes} onChange={e => setCompleteData({
              ...completeData,
              nextAppointmentNotes: e.target.value
            })} placeholder="Ej: Control post-tratamiento, siguiente sesión..." rows={2} data-id="element-489" />
              
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2" data-id="element-490">
            <Button variant="outline" onClick={() => setIsCompleteOpen(false)} data-id="element-491">
              Cancelar
            </Button>
            <Button onClick={handleComplete} leftIcon={<CheckIcon className="w-4 h-4" data-id="element-493" />} data-id="element-492">
              
              Completar Cita
            </Button>
          </div>
        </div>
      </Modal>

      {/* Excel Export Modal */}
      <ExcelExportModal isOpen={isExportModalOpen} onClose={() => setIsExportModalOpen(false)} onExport={handleExportToExcel} title="Exportar Citas" isExporting={isExporting} data-id="element-494" />
      

      {/* PDF Export Modal */}
      <PdfExportModal isOpen={isPdfExportModalOpen} onClose={() => setIsPdfExportModalOpen(false)} onExport={handleExportToPdf} title="Exportar Citas a PDF" isExporting={isPdfExporting} data-id="element-495" />
      
    </div>;
}